import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  Target,
  Eye,
  Heart,
  Zap,
  Users,
  Award,
  Clock,
  Globe,
  Smartphone,
  Server,
  Brain,
  Palette
} from "lucide-react";

// TODO: Replace with actual team member data
const teamMembers = [
  {
    name: { en: "Ahmed Fathy", ar: "أحمد فتحي" },
    role: { en: "Founder & CEO", ar: "المؤسس والرئيس التنفيذي" },
    image: null, // TODO: Add actual image
    bio: {
      en: "Visionary leader with 10+ years in digital innovation",
      ar: "قائد رؤيوي مع أكثر من 10 سنوات في الابتكار الرقمي"
    }
  },
  {
    name: { en: "Sarah Ahmed", ar: "سارة أحمد" },
    role: { en: "Lead Developer", ar: "مطور رئيسي" },
    image: null,
    bio: {
      en: "Full-stack expert specializing in Flutter & Go",
      ar: "خبير في التطوير الشامل متخصص في Flutter و Go"
    }
  },
  {
    name: { en: "Omar Hassan", ar: "عمر حسن" },
    role: { en: "UI/UX Designer", ar: "مصمم واجهات المستخدم" },
    image: null,
    bio: {
      en: "Creative designer passionate about user experience",
      ar: "مصمم مبدع شغوف بتجربة المستخدم"
    }
  },
  {
    name: { en: "Layla Ibrahim", ar: "ليلى إبراهيم" },
    role: { en: "Project Manager", ar: "مدير المشاريع" },
    image: null,
    bio: {
      en: "Agile specialist ensuring project success",
      ar: "متخصصة في منهجية أجايل لضمان نجاح المشاريع"
    }
  },
];

const values = [
  {
    icon: Target,
    title: { en: "Excellence", ar: "التميز" },
    description: {
      en: "We strive for excellence in every project, delivering solutions that exceed expectations.",
      ar: "نسعى للتميز في كل مشروع، ونقدم حلولاً تفوق التوقعات."
    }
  },
  {
    icon: Heart,
    title: { en: "Passion", ar: "الشغف" },
    description: {
      en: "Our passion for technology drives us to create innovative solutions that make a difference.",
      ar: "شغفنا بالتكنولوجيا يدفعنا لإنشاء حلول مبتكرة تحدث فرقاً."
    }
  },
  {
    icon: Users,
    title: { en: "Collaboration", ar: "التعاون" },
    description: {
      en: "We believe in the power of teamwork and close collaboration with our clients.",
      ar: "نؤمن بقوة العمل الجماعي والتعاون الوثيق مع عملائنا."
    }
  },
  {
    icon: Zap,
    title: { en: "Innovation", ar: "الابتكار" },
    description: {
      en: "We embrace cutting-edge technologies to deliver future-proof solutions.",
      ar: "نتبنى أحدث التقنيات لتقديم حلول مستدامة للمستقبل."
    }
  },
];

const stats = [
  { value: "5+", label: { en: "Projects Completed", ar: "مشروع منجز" } },
  { value: "20+", label: { en: "Happy Clients", ar: "عميل سعيد" } },
  { value: "5+", label: { en: "Years Experience", ar: "سنوات خبرة" } },
  { value: "95%", label: { en: "Client Satisfaction", ar: "رضا العملاء" } },
];

const technologies = [
  { icon: Smartphone, name: "Flutter", category: "Mobile" },
  { icon: Globe, name: "React/Next.js", category: "Web" },
  { icon: Server, name: "Go", category: "Backend" },
  { icon: Brain, name: "AI/ML", category: "Intelligence" },
  { icon: Palette, name: "Figma", category: "Design" },
];

export default function About() {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)',
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />
          <motion.div
            className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)',
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Link href="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                {isRTL ? 'الرئيسية' : 'Back to Home'}
              </Button>
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className={`${isRTL ? 'text-right lg:order-2' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.span
                className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {isRTL ? 'من نحن' : 'About Us'}
              </motion.span>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t("aboutTitle")} <span className="gradient-text">{t("aboutTitleHighlight")}</span>
              </h1>

              <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                {t("aboutDescription")}
              </p>

              <Link href="/#contact">
                <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-xl group">
                  {t("getInTouch")}
                  <ArrowRight className={`h-5 w-5 ml-2 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 mr-2 ml-0' : ''}`} />
                </Button>
              </Link>
            </motion.div>

            {/* Hero Image / Illustration */}
            <motion.div
              className={`relative ${isRTL ? 'lg:order-1' : ''}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative aspect-square max-w-lg mx-auto">
                {/* Placeholder for illustration/image */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 flex items-center justify-center">
                  <img
                    src="/assets/logo.webp"
                    alt="Ibtkary"
                    className="w-1/2 h-auto opacity-80"
                  />
                </div>

                {/* Floating elements */}
                <motion.div
                  className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.div
                  className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-secondary/10 border border-secondary/20"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label.en}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground">
                  {language === 'ar' ? stat.label.ar : stat.label.en}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              className={`p-8 rounded-2xl bg-card border border-border/50 ${isRTL ? 'text-right' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {isRTL ? 'مهمتنا' : 'Our Mission'}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {isRTL
                  ? 'تحويل الأفكار المبتكرة إلى حلول رقمية قوية تدفع نمو الأعمال وتخلق قيمة مستدامة لعملائنا من خلال التميز التقني والإبداع.'
                  : 'To transform innovative ideas into powerful digital solutions that drive business growth and create lasting value for our clients through technical excellence and creativity.'}
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              className={`p-8 rounded-2xl bg-card border border-border/50 ${isRTL ? 'text-right' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-secondary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                {isRTL ? 'رؤيتنا' : 'Our Vision'}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {isRTL
                  ? 'أن نكون الشريك التقني الرائد في المنطقة، معروفين بحلولنا المبتكرة وتفانينا في تحقيق نجاح عملائنا وتمكينهم رقمياً.'
                  : 'To be the leading technology partner in the region, known for our innovative solutions and dedication to client success and digital empowerment.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
              {isRTL ? 'قيمنا' : 'Our Values'}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              {isRTL ? 'ما يميزنا' : 'What Drives Us'}
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {values.map((value, index) => (
              <motion.div
                key={value.title.en}
                variants={fadeInUp}
                className={`p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {language === 'ar' ? value.title.ar : value.title.en}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {language === 'ar' ? value.description.ar : value.description.en}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
              {isRTL ? 'فريقنا' : 'Our Team'}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {isRTL ? 'تعرف على' : 'Meet The'} <span className="gradient-text">{isRTL ? 'الفريق' : 'Team'}</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'فريق من المحترفين المتحمسين والمبدعين يعملون معاً لتحقيق رؤيتك'
                : 'A team of passionate professionals working together to bring your vision to life'}
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name.en}
                variants={fadeInUp}
                className="group text-center"
              >
                <div className="relative mb-4 mx-auto w-40 h-40 rounded-2xl overflow-hidden">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={language === 'ar' ? member.name.ar : member.name.en}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-4xl font-bold text-primary">
                      {(language === 'ar' ? member.name.ar : member.name.en).charAt(0)}
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-semibold mb-1">
                  {language === 'ar' ? member.name.ar : member.name.en}
                </h3>
                <p className="text-primary text-sm font-medium mb-2">
                  {language === 'ar' ? member.role.ar : member.role.en}
                </p>
                <p className="text-muted-foreground text-sm">
                  {language === 'ar' ? member.bio.ar : member.bio.en}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Technologies Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4">
              {isRTL ? 'التقنيات' : 'Technologies'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              {isRTL ? 'أدواتنا' : 'Our Stack'}
            </h2>
          </motion.div>

          <motion.div
            className="flex flex-wrap justify-center gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {technologies.map((tech) => (
              <motion.div
                key={tech.name}
                variants={fadeInUp}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <tech.icon className="h-6 w-6 text-primary" />
                <div className={isRTL ? 'text-right' : ''}>
                  <div className="font-semibold">{tech.name}</div>
                  <div className="text-xs text-muted-foreground">{tech.category}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {isRTL ? 'جاهز للبدء معنا؟' : 'Ready to Start?'}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              {isRTL
                ? 'دعنا نحول فكرتك إلى واقع رقمي. تواصل مع فريقنا اليوم وابدأ رحلتك نحو النجاح.'
                : "Let's turn your idea into digital reality. Contact our team today and start your journey to success."}
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 justify-center ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <Link href="/#contact">
                <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-xl">
                  {t("getInTouch")}
                </Button>
              </Link>
              <Link href="/projects">
                <Button size="lg" variant="outline" className="h-14 px-8 text-base font-semibold rounded-xl">
                  {t("viewAllProjects")}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
